import { Plus, Minus, X } from 'lucide-react';
import { useState } from 'react';
import {
  AdvancedSearchProps,
  FieldConfig,
  FilterGroup,
  SearchRule,
  LogicalOperator,
  FieldOperator,
  getOperatorsForDataType,
  OPERATOR_LABELS,
  createEmptyFilterGroup,
  createEmptySearchRule,
} from './types';
import AsyncSearchSelect from './AsyncSearchSelect';

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  fieldConfigs,
  query,
  onSearch,
  onClear,
}) => {
  // Normalize query to ensure all values are defined (not null/undefined)
  const normalizeQuery = (q: typeof query) => ({
    ...q,
    filterGroups: q.filterGroups.map(group => ({
      ...group,
      rules: group.rules.map(rule => ({
        ...rule,
        value: rule.value ?? '',
      })),
    })),
  });

  const [localQuery, setLocalQuery] = useState(normalizeQuery(query));

  // Add a new filter group
  const handleAddFilterGroup = () => {
    setLocalQuery({
      ...localQuery,
      filterGroups: [...localQuery.filterGroups, createEmptyFilterGroup()],
    });
  };

  // Remove a filter group
  const handleRemoveFilterGroup = (groupId: string) => {
    setLocalQuery({
      ...localQuery,
      filterGroups: localQuery.filterGroups.filter(g => g.id !== groupId),
    });
  };

  // Update group logical operator
  const handleUpdateGroupOperator = (
    groupId: string,
    operator: LogicalOperator
  ) => {
    setLocalQuery({
      ...localQuery,
      filterGroups: localQuery.filterGroups.map(g =>
        g.id === groupId ? { ...g, logicalOperator: operator } : g
      ),
    });
  };

  // Update the operator between groups
  const handleUpdateGroupsOperator = (operator: LogicalOperator) => {
    setLocalQuery({
      ...localQuery,
      groupOperator: operator,
    });
  };

  // Add a rule to a group
  const handleAddRule = (groupId: string) => {
    setLocalQuery({
      ...localQuery,
      filterGroups: localQuery.filterGroups.map(g =>
        g.id === groupId
          ? { ...g, rules: [...g.rules, createEmptySearchRule()] }
          : g
      ),
    });
  };

  // Remove a rule from a group
  const handleRemoveRule = (groupId: string, ruleId: string) => {
    setLocalQuery({
      ...localQuery,
      filterGroups: localQuery.filterGroups.map(g =>
        g.id === groupId
          ? { ...g, rules: g.rules.filter(r => r.id !== ruleId) }
          : g
      ),
    });
  };

  // Update a rule field
  const handleUpdateRuleField = (
    groupId: string,
    ruleId: string,
    field: string
  ) => {
    const fieldConfig = fieldConfigs.find(f => f.key === field);
    const defaultOperator = fieldConfig
      ? getOperatorsForDataType(fieldConfig.dataType)[0]
      : 'equals';

    setLocalQuery({
      ...localQuery,
      filterGroups: localQuery.filterGroups.map(g =>
        g.id === groupId
          ? {
              ...g,
              rules: g.rules.map(r =>
                r.id === ruleId
                  ? { ...r, field, operator: defaultOperator, value: '' }
                  : r
              ),
            }
          : g
      ),
    });
  };

  // Update a rule operator
  const handleUpdateRuleOperator = (
    groupId: string,
    ruleId: string,
    operator: FieldOperator
  ) => {
    setLocalQuery({
      ...localQuery,
      filterGroups: localQuery.filterGroups.map(g =>
        g.id === groupId
          ? {
              ...g,
              rules: g.rules.map(r =>
                r.id === ruleId ? { ...r, operator } : r
              ),
            }
          : g
      ),
    });
  };

  // Update a rule value
  const handleUpdateRuleValue = (
    groupId: string,
    ruleId: string,
    value: string | number | Date | [Date, Date]
  ) => {
    setLocalQuery({
      ...localQuery,
      filterGroups: localQuery.filterGroups.map(g =>
        g.id === groupId
          ? {
              ...g,
              rules: g.rules.map(r => (r.id === ruleId ? { ...r, value } : r)),
            }
          : g
      ),
    });
  };

  // Get field config by key
  const getFieldConfig = (key: string): FieldConfig | undefined => {
    return fieldConfigs.find(f => f.key === key);
  };

  // Render value input based on field type
  const renderValueInput = (
    group: FilterGroup,
    rule: SearchRule
  ): JSX.Element => {
    const fieldConfig = getFieldConfig(rule.field);
    if (!fieldConfig) {
      return (
        <input
          type="text"
          placeholder="Select Value"
          value=""
          disabled
          style={styles.inputDisabledStyle}
        />
      );
    }

    // Handle isBetween operator for dates
    if (rule.operator === 'isBetween' && fieldConfig.dataType === 'date') {
      const dateRange = Array.isArray(rule.value) ? rule.value : ['', ''];
      return (
        <div style={styles.dateRangeContainerStyle}>
          <input
            type="date"
            value={(dateRange[0] as string) ?? ''}
            onChange={e => {
              const newRange = [e.target.value, dateRange[1]];
              handleUpdateRuleValue(
                group.id,
                rule.id,
                newRange as [Date, Date]
              );
            }}
            style={styles.dateRangeInputStyle}
            placeholder="Start date"
          />
          <span style={styles.dateRangeSeparatorStyle}>to</span>
          <input
            type="date"
            value={(dateRange[1] as string) ?? ''}
            onChange={e => {
              const newRange = [dateRange[0], e.target.value];
              handleUpdateRuleValue(
                group.id,
                rule.id,
                newRange as [Date, Date]
              );
            }}
            style={styles.dateRangeInputStyle}
            placeholder="End date"
          />
        </div>
      );
    }

    switch (fieldConfig.dataType) {
      case 'string':
        return (
          <input
            type="text"
            placeholder="Enter value"
            value={(rule.value as string) ?? ''}
            onChange={e =>
              handleUpdateRuleValue(group.id, rule.id, e.target.value)
            }
            style={styles.inputStyle}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            placeholder="Enter number"
            value={typeof rule.value === 'number' ? rule.value : ''}
            onChange={e =>
              handleUpdateRuleValue(
                group.id,
                rule.id,
                e.target.value ? parseFloat(e.target.value) : ''
              )
            }
            style={styles.inputStyle}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={(rule.value as string) ?? ''}
            onChange={e =>
              handleUpdateRuleValue(group.id, rule.id, e.target.value)
            }
            style={styles.inputStyle}
          />
        );
      case 'selection':
        // Check if this is an async select field
        if (
          fieldConfig.apiEndpoint &&
          fieldConfig.searchField &&
          fieldConfig.valueField &&
          fieldConfig.labelField
        ) {
          return (
            <AsyncSearchSelect
              key={`${rule.field}-${fieldConfig.apiEndpoint}`}
              value={rule.value as string}
              onChange={(newValue: string | null) =>
                handleUpdateRuleValue(group.id, rule.id, newValue || '')
              }
              apiEndpoint={fieldConfig.apiEndpoint}
              searchField={fieldConfig.searchField}
              valueField={fieldConfig.valueField}
              labelField={fieldConfig.labelField}
              placeholder="Select value"
            />
          );
        }

        // Static select dropdown (original behavior)
        return (
          <select
            value={(rule.value as string) ?? ''}
            onChange={e =>
              handleUpdateRuleValue(group.id, rule.id, e.target.value)
            }
            style={styles.selectStyle}>
            <option value="">Select value</option>
            {fieldConfig.options?.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="text"
            value=""
            disabled
            style={styles.inputDisabledStyle}
          />
        );
    }
  };

  const handleSearch = () => {
    onSearch(localQuery);
  };

  const handleClear = () => {
    const emptyQuery = {
      filterGroups: [createEmptyFilterGroup()],
      groupOperator: 'AND' as LogicalOperator,
    };
    setLocalQuery(emptyQuery);
    onClear();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div style={styles.containerStyle} onKeyDown={handleKeyDown}>
      {/* Filter Groups */}
      <div style={styles.filterGroupsContainerStyle}>
        {localQuery.filterGroups.map((group, groupIndex) => (
          <div key={group.id}>
            {/* Group Separator with AND/OR between groups */}
            {groupIndex > 0 && (
              <div style={styles.groupSeparatorStyle}>
                <select
                  value={localQuery.groupOperator}
                  onChange={e =>
                    handleUpdateGroupsOperator(
                      e.target.value as LogicalOperator
                    )
                  }
                  style={styles.logicalOperatorSelectStyle}>
                  <option value="AND">And</option>
                  <option value="OR">Or</option>
                </select>
              </div>
            )}

            {/* Filter Group */}
            <div style={styles.filterGroupStyle}>
              <div style={styles.filterGroupHeaderStyle}>
                <span style={styles.filterGroupLabelStyle}>
                  Filter Group {groupIndex + 1}
                </span>
                {localQuery.filterGroups.length > 1 && (
                  <button
                    onClick={() => handleRemoveFilterGroup(group.id)}
                    style={styles.removeGroupButtonStyle}
                    title="Remove Filter Group">
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Rules */}
              <div style={styles.rulesContainerStyle}>
                {group.rules.length === 0 ? (
                  <div style={styles.emptyRulesStyle}>
                    No rules yet. Click "Add Rule" to get started.
                  </div>
                ) : (
                  group.rules.map((rule, ruleIndex) => (
                    <div key={rule.id}>
                      {/* AND/OR between rules in same group */}
                      {ruleIndex > 0 && (
                        <div style={styles.ruleSeparatorStyle}>
                          <select
                            value={group.logicalOperator}
                            onChange={e =>
                              handleUpdateGroupOperator(
                                group.id,
                                e.target.value as LogicalOperator
                              )
                            }
                            style={styles.andOrSelectStyle}>
                            <option value="AND">And</option>
                            <option value="OR">Or</option>
                          </select>
                        </div>
                      )}

                      {/* Rule Row */}
                      <div style={styles.ruleRowStyle}>
                        <div style={styles.ruleFieldsStyle}>
                          {/* Field Selector */}
                          <div style={styles.fieldColumnStyle}>
                            <select
                              value={rule.field ?? ''}
                              onChange={e =>
                                handleUpdateRuleField(
                                  group.id,
                                  rule.id,
                                  e.target.value
                                )
                              }
                              style={styles.selectStyle}>
                              <option value="">Select Field</option>
                              {fieldConfigs.map(field => (
                                <option key={field.key} value={field.key}>
                                  {field.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Operator Selector */}
                          <div style={styles.operatorColumnStyle}>
                            <select
                              value={rule.operator ?? ''}
                              onChange={e =>
                                handleUpdateRuleOperator(
                                  group.id,
                                  rule.id,
                                  e.target.value as FieldOperator
                                )
                              }
                              disabled={!rule.field}
                              style={
                                rule.field
                                  ? styles.selectStyle
                                  : styles.selectDisabledStyle
                              }>
                              <option value="">Select Operator</option>
                              {rule.field &&
                                (() => {
                                  const fieldConfig = getFieldConfig(
                                    rule.field
                                  );
                                  return fieldConfig
                                    ? getOperatorsForDataType(
                                        fieldConfig.dataType
                                      ).map(op => (
                                        <option key={op} value={op}>
                                          {OPERATOR_LABELS[op]}
                                        </option>
                                      ))
                                    : null;
                                })()}
                            </select>
                          </div>

                          {/* Value Input */}
                          <div style={styles.valueColumnStyle}>
                            {renderValueInput(group, rule)}
                          </div>
                        </div>

                        {/* Remove Rule Button */}
                        <button
                          onClick={() => handleRemoveRule(group.id, rule.id)}
                          style={styles.removeRuleButtonStyle}
                          title="Remove Rule">
                          <Minus size={16} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Add Rule Button */}
              <button
                onClick={() => handleAddRule(group.id)}
                style={styles.addRuleButtonStyle}>
                <Plus size={16} /> Add Rule
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons Row */}
      <div style={styles.actionButtonsRowStyle}>
        {/* Add Filter Group Button */}
        <button
          onClick={handleAddFilterGroup}
          style={styles.addFilterGroupButtonStyle}>
          <Plus size={16} /> Add Filter Group
        </button>

        {/* Clear and Search Buttons */}
        <div style={styles.actionButtonsStyle}>
          <button onClick={handleClear} style={styles.clearButtonStyle}>
            Clear
          </button>
          <button onClick={handleSearch} style={styles.searchButtonStyle}>
            Search
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  containerStyle: {
    border: '1px solid #d1d5db',
    borderRadius: '0.5rem',
    padding: '1rem',
    backgroundColor: '#ffffff',
  } as React.CSSProperties,
  filterGroupsContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  } as React.CSSProperties,
  filterGroupStyle: {
    border: '1px solid #e5e7eb',
    borderRadius: '0.375rem',
    padding: '0.75rem',
    backgroundColor: '#f9fafb',
  } as React.CSSProperties,
  filterGroupHeaderStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  } as React.CSSProperties,
  filterGroupLabelStyle: {
    fontSize: '0.8125rem',
    fontWeight: '600',
    color: '#374151',
  } as React.CSSProperties,
  removeGroupButtonStyle: {
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '0.25rem',
    padding: '0.25rem 0.5rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.75rem',
  } as React.CSSProperties,
  rulesContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  } as React.CSSProperties,
  emptyRulesStyle: {
    padding: '0.75rem',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '0.8125rem',
    fontStyle: 'italic',
  } as React.CSSProperties,
  ruleRowStyle: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    padding: '0.5rem',
    borderRadius: '0.25rem',
    border: '1px solid #e5e7eb',
  } as React.CSSProperties,
  ruleFieldsStyle: {
    display: 'flex',
    gap: '0.5rem',
    flex: 1,
  } as React.CSSProperties,
  fieldColumnStyle: {
    flex: '1 1 180px',
  } as React.CSSProperties,
  operatorColumnStyle: {
    flex: '0 0 140px',
  } as React.CSSProperties,
  valueColumnStyle: {
    flex: '1 1 240px',
  } as React.CSSProperties,
  selectStyle: {
    padding: '0.4rem 0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    fontSize: '0.8125rem',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    width: '100%',
  } as React.CSSProperties,
  selectDisabledStyle: {
    padding: '0.4rem 0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    fontSize: '0.8125rem',
    backgroundColor: '#f3f4f6',
    color: '#9ca3af',
    cursor: 'not-allowed',
    width: '100%',
  } as React.CSSProperties,
  inputStyle: {
    padding: '0.4rem 0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    fontSize: '0.8125rem',
    width: '100%',
  } as React.CSSProperties,
  inputDisabledStyle: {
    padding: '0.4rem 0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    fontSize: '0.8125rem',
    backgroundColor: '#f3f4f6',
    color: '#9ca3af',
    cursor: 'not-allowed',
    width: '100%',
  } as React.CSSProperties,
  removeRuleButtonStyle: {
    backgroundColor: 'transparent',
    color: '#ef4444',
    border: '1px solid #ef4444',
    borderRadius: '0.25rem',
    padding: '0.4rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  } as React.CSSProperties,
  groupSeparatorStyle: {
    display: 'flex',
    justifyContent: 'center',
    margin: '0.375rem 0',
  } as React.CSSProperties,
  logicalOperatorSelectStyle: {
    padding: '0.25rem 0.625rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    fontSize: '0.8125rem',
    fontWeight: '500',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    minWidth: '70px',
    textAlign: 'center',
  } as React.CSSProperties,
  ruleSeparatorStyle: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginLeft: '0.75rem',
    marginBottom: '0.375rem',
  } as React.CSSProperties,
  andOrSelectStyle: {
    padding: '0.2rem 0.4rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    fontSize: '0.6875rem',
    fontWeight: '500',
    backgroundColor: '#fef3c7',
    cursor: 'pointer',
    minWidth: '55px',
  } as React.CSSProperties,
  addRuleButtonStyle: {
    backgroundColor: 'transparent',
    color: '#2563eb',
    border: '1px solid #2563eb',
    borderRadius: '0.25rem',
    padding: '0.375rem 0.75rem',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    marginTop: '0.5rem',
  } as React.CSSProperties,
  addFilterGroupButtonStyle: {
    backgroundColor: 'transparent',
    color: '#059669',
    border: '1px solid #059669',
    borderRadius: '0.25rem',
    padding: '0.375rem 0.75rem',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  } as React.CSSProperties,
  actionButtonsRowStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '1rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid #e5e7eb',
  } as React.CSSProperties,
  actionButtonsStyle: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
  } as React.CSSProperties,
  clearButtonStyle: {
    padding: '0.4rem 0.875rem',
    backgroundColor: '#e5e7eb',
    color: '#374151',
    border: 'none',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: '500',
  } as React.CSSProperties,
  searchButtonStyle: {
    padding: '0.4rem 1.25rem',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: '500',
  } as React.CSSProperties,
  dateRangeContainerStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    width: '100%',
  } as React.CSSProperties,
  dateRangeInputStyle: {
    padding: '0.4rem 0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    fontSize: '0.8125rem',
    flex: 1,
  } as React.CSSProperties,
  dateRangeSeparatorStyle: {
    fontSize: '0.6875rem',
    color: '#6b7280',
    fontWeight: '500',
    flexShrink: 0,
  } as React.CSSProperties,
};

export default AdvancedSearch;
